import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AppraisalWorkspaceError,
  addPaymentLink,
  downloadAppraisalDocument,
  downloadAppraisalResource,
  getAppraisalSnapshot,
  listUnresolvedPaymentLinks,
  previewPaymentLinkReminder,
  refreshAppraisalWorkspace,
  resolveAppraisalXml,
  resolvePaymentLinkReminder,
  runAppraisalAudit,
  sendPaymentLinkReminder,
  uploadAppraisalXml
} from '@/services/appraisalWorkspaceService';
import type {
  AppraisalPaymentReminderOutcome,
  AppraisalSnapshot
} from '../../../../../supabase/functions/_shared/appraisal-workspace-contract';
import { parseLoanCenterDeepLink } from '../loanCenterDeepLink';
import { AppraisalWorkspace } from './AppraisalWorkspace';

export function AppraisalTab({ loanId, encompassLoanId, initialReportId, initialFeeVerificationId }: {
  loanId: string; encompassLoanId: string; initialReportId?: string; initialFeeVerificationId?: string
}) {
  const loan = useMemo(() => ({ loanId, encompassLoanId }), [loanId, encompassLoanId]);
  const [snapshot, setSnapshot] = useState<AppraisalSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const readSequence = useRef(0);
  const scopeSequence = useRef(0);
  const operationBusy = useRef(false);
  const initialLink = useMemo(() => parseLoanCenterDeepLink(window.location.search), []);
  const invalidatePendingRequests = useCallback(() => {
    scopeSequence.current++;
    readSequence.current++;
  }, []);

  const load = useCallback(async (): Promise<AppraisalSnapshot | null> => {
    const request = ++readSequence.current;
    try {
      const result = await getAppraisalSnapshot(loan);
      if (readSequence.current !== request) {
        return null;
      }
      setSnapshot(result);
      setError(null);
      return result;
    } catch (cause) {
      if (readSequence.current !== request) {
        return null;
      }
      if (cause instanceof AppraisalWorkspaceError && [401, 403].includes(cause.status || 0)) {
        setSnapshot(null);
      }
      setError(cause instanceof Error ? cause.message : 'Appraisal information is unavailable.');
      return null;
    } finally {
      if (readSequence.current === request) {
        setLoading(false);
      }
    }
  }, [loan]);

  useEffect(() => {
    const scope = ++scopeSequence.current;
    setSnapshot(null);
    setLoading(true);
    setError(null);
    setBusy(null);
    operationBusy.current = false;
    void load().then(async (result) => {
      if (
        scopeSequence.current !== scope ||
        !result ||
        !result.eligible ||
        result.mode === 'off' ||
        result.refreshPending
      ) {
        return;
      }
      if (!result.refreshedAt || Date.now() - Date.parse(result.refreshedAt) > 15 * 60000) {
        try {
          await refreshAppraisalWorkspace(loan);
          if (scopeSequence.current === scope) {
            await load();
          }
        } catch (cause) {
          if (scopeSequence.current === scope) {
            setError(cause instanceof Error ? cause.message : 'Refresh failed.');
          }
        }
      }
    });
    return invalidatePendingRequests;
  }, [load, loan, invalidatePendingRequests]);

  useEffect(() => {
    if (!snapshot || (!(snapshot.refreshPending && snapshot.eligible && snapshot.mode !== 'off') && !snapshot.feeCompliance?.some(row => row.pending))) {
      return;
    }
    const timer = window.setTimeout(() => {
      if (document.visibilityState === 'visible') {
        void load();
      }
    }, 10000);
    return () => window.clearTimeout(timer);
  }, [snapshot, load]);

  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') {
        void load();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  // Dialogs that show their own errors pass reportError=false, so the page banner does not repeat them.
  const perform = async (name: string, action: () => Promise<void>, success?: string, reportError = true): Promise<void> => {
    if (operationBusy.current) {
      throw new Error('An appraisal action is already running.');
    }
    const scope = scopeSequence.current;
    operationBusy.current = true;
    setBusy(name);
    setError(null);
    try {
      await action();
      if (scopeSequence.current === scope) {
        if (success) {
          toast.success(success);
        }
        await load();
      }
    } catch (cause) {
      if (scopeSequence.current === scope) {
        // A payment dialog's 403 (flag off, admin only) is its own message, not lost loan access.
        if (cause instanceof AppraisalWorkspaceError && (cause.status === 401 || (cause.status === 403 && reportError))) {
          setSnapshot(null);
        }
        if (reportError) {
          setError(cause instanceof Error ? cause.message : 'The appraisal action failed.');
        }
      }
      throw cause;
    } finally {
      if (scopeSequence.current === scope) {
        operationBusy.current = false;
        setBusy(null);
      }
    }
  };
  const quiet = (promise: Promise<void>) => {
    void promise.catch(() => undefined);
  };
  // Stable identities: the payment dialogs load on mount and must not reload on every render.
  const previewReminder = useCallback((requestId: string) => previewPaymentLinkReminder(loan, requestId), [loan]);
  const listUnresolved = useCallback(() => listUnresolvedPaymentLinks(loan), [loan]);
  return (
    <AppraisalWorkspace
      snapshot={snapshot}
      loading={loading}
      error={error}
      busy={busy}
      initialReportId={initialReportId || (initialLink?.loanId === loanId ? initialLink.reportId : undefined)}
      initialFeeVerificationId={initialFeeVerificationId}
      onRefresh={() => quiet(perform('refresh', () => refreshAppraisalWorkspace(loan)))}
      onAudit={(reportId) =>
        quiet(perform('audit', () => runAppraisalAudit(loan, reportId), 'Appraisal audit queued.'))
      }
      onUpload={(file, reportId) =>
        perform(
          'upload',
          () => uploadAppraisalXml(loan, file, reportId),
          'XML uploaded. The appraisal audit is queued.'
        )
      }
      onResolve={(reportId, note) =>
        perform('resolve', () => resolveAppraisalXml(loan, reportId, note), 'Manual handling recorded.')
      }
      onDownload={(reportId, kind) => quiet(perform('download', () => downloadAppraisalDocument(loan, reportId, kind)))}
      onResourceDownload={(orderId, resourceId, name) =>
        quiet(perform('download', () => downloadAppraisalResource(loan, orderId, resourceId, name)))
      }
      onPreviewReminder={previewReminder}
      onSendReminder={async (input) => {
        const result: { outcome?: AppraisalPaymentReminderOutcome } = {};
        await perform('reminder', async () => {
          result.outcome = await sendPaymentLinkReminder(loan, input);
        }, undefined, false);
        if (!result.outcome) {
          throw new Error('The reminder result was not returned. Refresh before sending again.');
        }
        return result.outcome;
      }}
      onAddPaymentLink={async (input) => {
        try {
          await perform('payment-link', async () => {
            await addPaymentLink(loan, input);
          }, 'Payment link saved.', false);
        } catch (cause) {
          // The server can keep the new link and then refuse the replace: show what it now holds before a retry.
          await load();
          throw cause;
        }
      }}
      onListUnresolvedPaymentLinks={listUnresolved}
      onResolveReminder={(input) =>
        perform('payment-link', () => resolvePaymentLinkReminder(loan, input), 'Reminder closed.', false)
      }
    />
  );
}
